/** GPU brightness/contrast — fragment-shader port of
 *  `lib/adjustments/brightness-contrast.ts`.
 *
 *  Match contract: per-pixel output is byte-equal to the JS LUT
 *  implementation. The shader rebuilds the same math in `[0, 255]`
 *  space (despite running on normalized floats internally) so the
 *  Photoshop factor formula's pivot-around-128 lands in the same
 *  integer slots and the final round-to-nearest matches.
 *
 *  Parity tests in `*.test.ts` enforce zero-tolerance equality on
 *  a representative pixel grid plus boundary cases (b=±100, c=±100,
 *  identity, mid-grey input). If the shader and CPU paths ever
 *  diverge byte-for-byte, those tests fail loudly. */

import type { BrightnessContrastValue } from '../../adjustments/brightness-contrast';
import { applyShader } from '../runtime';

/** Closed-form GLSL port of the JS LUT. The JS path:
 *   1. brightness-pre: v += b*(255-v) when b>0; v += b*v when b<0.
 *   2. contrast:       v = 128 + factor*(v-128)  with the
 *                      Photoshop factor formula.
 *   3. clamp + round.
 *
 *  We do everything in `[0, 255]` space inside the shader so the
 *  contrast pivot lands on 128 exactly and the final `floor(x+0.5)`
 *  matches `Math.round`. */
const BRIGHTNESS_CONTRAST_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_brightness; // raw value in [-100, 100]
uniform float u_contrast;   // raw value in [-100, 100]
out vec4 outColor;

float applyChannel(float v255) {
  float b = u_brightness / 100.0;
  // Brightness branch: positive blends toward 255, negative toward 0.
  // The JS uses an if/elseif; replicating with mix lets the GPU avoid
  // a divergent branch but produces identical math.
  v255 += b > 0.0 ? b * (255.0 - v255) : b * v255;
  // Contrast pivot around 128 with the Photoshop factor.
  float c = (u_contrast / 100.0) * 255.0;
  float factor = (259.0 * (c + 255.0)) / (255.0 * (259.0 - c));
  v255 = 128.0 + factor * (v255 - 128.0);
  // Match JS clamp + round-to-nearest.
  return clamp(floor(v255 + 0.5), 0.0, 255.0);
}

void main() {
  vec4 c = texture(u_source, v_uv);
  // Texture sampling gives normalized [0, 1] floats; convert to
  // [0, 255] integer space to match the JS LUT exactly.
  vec3 v255 = c.rgb * 255.0;
  v255.r = applyChannel(v255.r);
  v255.g = applyChannel(v255.g);
  v255.b = applyChannel(v255.b);
  outColor = vec4(v255 / 255.0, c.a);
}
`;

/** Run brightness/contrast on the GPU. Returns `true` if the GPU
 *  path succeeded and `target` now holds the result; `false` if the
 *  caller should fall back to the JS dispatch.
 *
 *  Same calling contract as the JS variant — source is an
 *  ImageData, target is the destination canvas, value is the typed
 *  config. Target is resized to source dims if they don't already
 *  match. */
export function applyBrightnessContrastOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: BrightnessContrastValue,
): boolean {
  return applyShader({
    source,
    target,
    fragShaderSrc: BRIGHTNESS_CONTRAST_FRAG,
    uniforms: {
      u_brightness: { type: 'float', value: value.brightness },
      u_contrast: { type: 'float', value: value.contrast },
    },
  });
}
